import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEO from '../shared/SEO';
import AdUnit from '../../components/AdUnit';
import { useTheme } from '../../hooks/useTheme';
import ArticleCard from './components/ArticleCard';
import articleData from '../../data/articleData';
import { AD_POSITIONS } from '../../utils/adConfig';
import { generateArticleListPageSchema } from '../../lib/seo';

const { FiSearch, FiX } = FiIcons;

const CATEGORIES = ['全部', ...new Set(articleData.map(a => a.category))];

function ArticleListPage() {
  const { theme } = useTheme();
  const [searchQuery,      setSearchQuery]      = useState('');
  const [activeCategory,   setActiveCategory]   = useState('全部');
  const [filteredArticles, setFilteredArticles] = useState(articleData);

  useEffect(() => {
    let result = articleData;
    if (activeCategory !== '全部') {
      result = result.filter(a => a.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(a =>
        a.title.toLowerCase().includes(q) ||
        a.excerpt.toLowerCase().includes(q) ||
        a.tags.some(t => t.toLowerCase().includes(q))
      );
    }
    setFilteredArticles(result);
  }, [searchQuery, activeCategory]);

  // ✅ 修正：改用 generateArticleListPageSchema（@graph 整合版）
  // 包含 Organization + WebSite + Breadcrumb + CollectionPage，無需再傳 breadcrumb prop
  const listSchema = generateArticleListPageSchema(articleData);

  return (
    <>
      <SEO
        title="Omakase 深度文章 - 日本料理文化完整指南"
        description="探索 Omakase 日本料理文化的深度文章，涵蓋初學者指南、春季食材、壽司與懷石比較、台灣文化發展、頂級食材解析及用餐禮儀等主題。"
        keywords="Omakase文章,日本料理指南,壽司文化,懷石料理,季節食材,用餐禮儀,台灣日本料理,食材解析"
        url="/articles"
        type="website"
        image="https://images.unsplash.com/photo-1579952363873-27d3bfad9c0d?w=1200&h=630&fit=crop"
        schema={listSchema}
      />

      <div className={`min-h-screen ${theme === 'dark' ? 'dark' : ''}`}>
        <Navigation />

        <main className="pt-20">
          <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">

            {/* 頁首 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center mb-12"
            >
              {/* ✅ 可見麵包屑（UI 層） */}
              <nav aria-label="麵包屑" className="flex justify-center mb-6">
                <ol className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                  <li>
                    <a href="/" className="hover:text-orange-500 transition-colors">首頁</a>
                  </li>
                  <li aria-hidden="true">/</li>
                  <li className="text-slate-700 dark:text-slate-200" aria-current="page">深度文章</li>
                </ol>
              </nav>

              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
                深度文章
              </h1>
              <p className="text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
                探索 Omakase 文化的每個面向，從入門到精通，帶您深入了解日本料理的精髓。
              </p>
            </motion.div>

            {/* 搜尋 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="max-w-2xl mx-auto mb-8"
            >
              <div className="relative">
                <SafeIcon
                  icon={FiSearch}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5"
                />
                <input
                  type="search"
                  placeholder="搜尋文章..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  aria-label="搜尋文章"
                  className="w-full pl-12 pr-12 py-4 rounded-2xl bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    aria-label="清除搜尋"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                  </button>
                )}
              </div>
            </motion.div>

            {/* 分類篩選 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="flex flex-wrap justify-center gap-3 mb-12"
              role="group"
              aria-label="文章分類篩選"
            >
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  aria-pressed={activeCategory === cat}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                    activeCategory === cat
                      ? 'bg-orange-500 text-white shadow-lg'
                      : 'bg-white/80 dark:bg-slate-800/80 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 hover:border-orange-400'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </motion.div>

            {/* 廣告 */}
            <div className="mb-12">
              <AdUnit {...AD_POSITIONS.ARTICLE_LIST?.banner || AD_POSITIONS.HOME.banner1} />
            </div>

            {/* 文章列表 */}
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredArticles.map((article, index) => (
                  <motion.div
                    key={article.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                  >
                    <ArticleCard article={article} headingLevel={2} />
                  </motion.div>
                ))}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-20"
              >
                <p className="text-2xl text-slate-400 mb-4">😔</p>
                <p className="text-slate-600 dark:text-slate-400 text-lg">
                  找不到符合「{searchQuery}」的文章
                </p>
                <button
                  onClick={() => { setSearchQuery(''); setActiveCategory('全部'); }}
                  className="mt-4 text-orange-500 hover:text-orange-600 font-medium"
                >
                  清除篩選條件
                </button>
              </motion.div>
            )}
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}

export default ArticleListPage;